
import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../mocks/translations';

export default function ActivitySection() {
  const { language } = useLanguage();
  const t = translations[language].activity;

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h2>
        <p className="text-lg text-gray-600">{t.subtitle}</p>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Main Activity Card with Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="relative h-[450px] rounded-xl overflow-hidden shadow-lg">
            <img 
              src="https://public.readdy.ai/ai/img_res/70a72e3dec7426b7b51353821a4fd7c1.jpg"
              alt="Oil Trading Facility"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg">
                  <i className="ri-oil-line text-xl text-white"></i>
                </div>
                <span className="text-white font-bold text-lg">{t.mainTitle}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-xl border border-teal-200 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.mainTitle}</h3>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              {t.mainDescription}
            </p>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0 mt-1">
                  <i className="ri-check-line text-sm text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{t.tradingTitle}</h4>
                  <p className="text-sm text-gray-600">{t.tradingDescription}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0 mt-1">
                  <i className="ri-check-line text-sm text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{t.supplyChainTitle}</h4>
                  <p className="text-sm text-gray-600">{t.supplyChainDescription}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0 mt-1">
                  <i className="ri-check-line text-sm text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{t.qualityTitle}</h4>
                  <p className="text-sm text-gray-600">{t.qualityDescription}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0 mt-1">
                  <i className="ri-check-line text-sm text-teal-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{t.marketTitle}</h4>
                  <p className="text-sm text-gray-600">{t.marketDescription}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Three Feature Cards with Images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-40 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Compliance%20and%20regulatory%20documents%20on%20desk%20with%20official%20stamps%20and%20certificates%2C%20professional%20legal%20business%20setting%2C%20clean%20organized%20office%20environment%2C%20warm%20natural%20lighting%2C%20corporate%20style&width=400&height=200&seq=activity2&orientation=landscape"
                alt="Compliance"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mx-auto mb-4 -mt-10 relative z-10 border-4 border-white">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t.compliance}</h4>
              <p className="text-sm text-gray-600">{t.complianceDescription}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-40 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Global%20network%20connections%20visualization%20with%20world%20map%20and%20glowing%20connection%20lines%2C%20international%20business%20network%2C%20modern%20digital%20technology%20concept%2C%20teal%20and%20emerald%20color%20scheme%2C%20professional%20style&width=400&height=200&seq=activity3&orientation=landscape"
                alt="Global Network"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg mx-auto mb-4 -mt-10 relative z-10 border-4 border-white">
                <i className="ri-global-line text-2xl text-emerald-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t.globalNetwork}</h4>
              <p className="text-sm text-gray-600">{t.globalNetworkDescription}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-40 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Award%20trophy%20and%20excellence%20certificate%20on%20executive%20desk%2C%20business%20achievement%20and%20recognition%2C%20golden%20award%20with%20professional%20corporate%20background%2C%20warm%20lighting%2C%20success%20concept&width=400&height=200&seq=activity4&orientation=landscape"
                alt="Excellence"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mx-auto mb-4 -mt-10 relative z-10 border-4 border-white">
                <i className="ri-award-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t.excellence}</h4>
              <p className="text-sm text-gray-600">{t.excellenceDescription}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
